import React, { useMemo, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import StepAgentDetailPanel from "./Workflows/StepAgentDetailPanel";
import StepApprovalDetailPanel from "./Workflows/StepApprovalDetailPanel";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import CircularProgress from "@mui/material/CircularProgress";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import BuildIcon from "@mui/icons-material/Build";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import LoopIcon from "@mui/icons-material/Loop";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import { WorkflowRunDetails } from "./Workflows/WorkflowTypes";
import { getAgent } from "../api/agentApi";

// ─── Per-step-type color palette (mirrors workflowUtils nodeTypeOptions) ──────

const STEP_COLORS: Record<string, { dot: string; text: string; bg: string; border: string }> = {
  agent:       { dot: "#B87333", text: "#7a4a1e", bg: "rgba(184,115,51,0.07)",  border: "rgba(184,115,51,0.28)" },
  tool:        { dot: "#789769", text: "#3d5c34", bg: "rgba(120,151,105,0.07)", border: "rgba(120,151,105,0.28)" },
  human_input: { dot: "#8E588E", text: "#5a3060", bg: "rgba(142,88,142,0.07)", border: "rgba(142,88,142,0.28)" },
  conditional: { dot: "#D4A017", text: "#7a5c00", bg: "rgba(212,160,23,0.07)", border: "rgba(212,160,23,0.28)" },
  loop:        { dot: "#4A90D9", text: "#1a4f8a", bg: "rgba(74,144,217,0.07)", border: "rgba(74,144,217,0.28)" },
  merge:       { dot: "#2E8B57", text: "#1a5235", bg: "rgba(46,139,87,0.07)",  border: "rgba(46,139,87,0.28)"  },
  wait:        { dot: "#6B7B8D", text: "#3d4c5a", bg: "rgba(107,123,141,0.07)",border: "rgba(107,123,141,0.28)"},
};

const stepColor = (stepType: string) =>
  STEP_COLORS[stepType] ?? STEP_COLORS["agent"];

const stepIcon = (stepType: string, size = 14): React.ReactElement => {
  const props = { sx: { fontSize: size } };
  switch (stepType) {
    case "tool":        return <BuildIcon {...props} />;
    case "human_input": return <PersonOutlineIcon {...props} />;
    case "conditional": return <AltRouteIcon {...props} />;
    case "loop":        return <LoopIcon {...props} />;
    case "merge":       return <CallMergeIcon {...props} />;
    default:            return <SmartToyIcon {...props} />;
  }
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface DerivedStep {
  id: string;
  stepType: "agent" | "tool" | string;
  label: string;
  stepName?: string;        // original DAG step name (shown as sub-line)
  agentUuid?: string;       // real agent UUID (distinct from step.id which is the DAG step key)
  status: "queued" | "running" | "complete" | "error" | "paused" | "waiting";
  startedAt?: string | null;
  completedAt?: string | null;
  outputPreview?: string;
  promptPreview?: string;
  errorMessage?: string | null;
  agentVersion?: number;
  agentRunNumber?: number;
  approvalId?: string | null;
  parentLoop?: string | null;
}

export interface WorkflowRunTimelineProps {
  runDetails: WorkflowRunDetails | null;
  workflowName?: string;
  dagDefinition?: { steps?: any[] };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso?: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return "";
  }
}

function durationLabel(startIso?: string | null, endIso?: string | null): string {
  if (!startIso) return "";
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const secs = Math.round((end - start) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

/**
 * Build a stable display order from DAG step definitions.
 *
 * `dagSteps` is the authoritative source — an array from dag_definition.steps
 * (which has loop_body_steps, depends_on, type, id). Falls back to deriving
 * from stepsMap (execution_logs.steps) if no dagSteps provided.
 *
 * Rules:
 *  - Body steps of a loop are displayed BEFORE their parent loop step.
 *  - Top-level steps are ordered by depends_on topology.
 *  - Loop body steps are ordered by their own depends_on within the loop body.
 *
 * Returns Map<stepId, displayIndex>.
 */
function buildDagDisplayOrder(
  dagSteps: any[] | null,
  stepsMap: Record<string, any>
): Map<string, number> {
  // Normalise into a flat id→step dict
  const stepDefs: Record<string, any> = {};
  if (dagSteps && dagSteps.length > 0) {
    dagSteps.forEach((s) => { if (s?.id) stepDefs[s.id] = s; });
  } else {
    // Fallback: execution_logs.steps (loop_body_steps likely missing — order will be flat)
    Object.assign(stepDefs, stepsMap);
  }

  const order = new Map<string, number>();
  let counter = 0;

  // Build loop→body and body→parent maps
  const loopBodySteps = new Map<string, string[]>(); // loopId → [bodyStepIds]
  const bodyStepParent = new Map<string, string>();   // bodyStepId → loopId
  Object.entries(stepDefs).forEach(([sid, s]) => {
    const bodyIds: string[] = s.loop_body_steps ?? [];
    if (bodyIds.length > 0) {
      loopBodySteps.set(sid, bodyIds);
      bodyIds.forEach((bid) => bodyStepParent.set(bid, sid));
    }
  });

  // Topological sort of body steps within each loop
  function visitBody(bodyIds: string[]) {
    const bodySet = new Set(bodyIds);
    const visited = new Set<string>();
    function visitOne(id: string) {
      if (visited.has(id)) return;
      visited.add(id);
      (stepDefs[id]?.depends_on ?? []).forEach((dep: string) => {
        if (bodySet.has(dep)) visitOne(dep);
      });
      if (!order.has(id)) order.set(id, counter++);
    }
    bodyIds.forEach(visitOne);
  }

  // Topological sort of top-level steps.
  // When we encounter a loop step, first lay out its body steps, then the loop itself.
  const topLevel = Object.keys(stepDefs).filter((sid) => !bodyStepParent.has(sid));
  const visitedTop = new Set<string>();

  function visitTop(sid: string) {
    if (visitedTop.has(sid)) return;
    visitedTop.add(sid);
    (stepDefs[sid]?.depends_on ?? []).forEach((dep: string) => {
      if (topLevel.includes(dep)) visitTop(dep);
    });
    if (loopBodySteps.has(sid)) {
      visitBody(loopBodySteps.get(sid)!);
    }
    if (!order.has(sid)) order.set(sid, counter++);
  }
  topLevel.forEach(visitTop);

  return order;
}

/**
 * Convert execution_logs.steps dict → DerivedStep array.
 *
 * Rules:
 *  - Started steps (have started_at) → shown with their actual status.
 *  - The single NEXT not-yet-started step (first one after all started steps,
 *    ordered by the DAG display order) → shown as "queued".
 *  - All other not-yet-started steps → hidden.
 *
 * Ordering is derived from the DAG definition (stable, structure-based),
 * never from runtime timestamps or event order.
 */
function deriveSteps(runDetails: WorkflowRunDetails | null, dagDefinition?: { steps?: any[] }): DerivedStep[] {
  if (!runDetails) return [];

  const stepsMap: Record<string, any> =
    (runDetails.execution_logs as any)?.steps ?? {};

  // All step keys known to us
  const allKeys = Object.keys(stepsMap);

  // ── Stable display order from DAG structure ──────────────────────────────
  // Use dag_definition.steps (has loop_body_steps) as the source of truth.
  // Fall back to stepsMap from execution_logs only if dag definition absent.
  const dagOrder = buildDagDisplayOrder(dagDefinition?.steps ?? null, stepsMap);

  // ── Terminal loop detection ──────────────────────────────────────────────
  const terminalLoopIds = new Set<string>();
  allKeys.forEach((key) => {
    const s = stepsMap[key];
    const raw = (s.status ?? "").toLowerCase();
    if (s.step_type === "loop") {
      if (raw === "success" || raw === "completed" || raw === "failed" || raw === "error") {
        terminalLoopIds.add(key);
      }
    }
  });

  // ── Body-step → parent loop membership (from DAG definition) ────────────
  // Used to derive parentLoop for started steps, and to filter the queued-step picker.
  const bodyStepParent = new Map<string, string>(); // bodyStepId → loopId
  if (dagDefinition?.steps) {
    dagDefinition.steps.forEach((s: any) => {
      (s.loop_body_steps ?? []).forEach((bid: string) => {
        bodyStepParent.set(bid, s.id);
      });
    });
  }

  // ── Build started steps ──────────────────────────────────────────────────
  const startedSteps: DerivedStep[] = allKeys
    .filter((key) => stepsMap[key].started_at)
    .map((key) => {
      const s = stepsMap[key];
      const rawStatus: string = (s.status ?? "").toLowerCase();
      const parentLoop: string | null =
        s.parent_loop ?? bodyStepParent.get(key) ?? null;

      let status: DerivedStep["status"] = "running";
      if (rawStatus === "success" || rawStatus === "completed") {
        status = "complete";
      } else if (rawStatus === "error" || rawStatus === "failed") {
        status = "error";
      } else if (rawStatus === "paused") {
        status = "paused";
      } else if (rawStatus === "waiting_for_approval" || rawStatus === "waiting_for_input") {
        status = "waiting";
      }

      // Override stale body step statuses when parent loop has finished
      if (parentLoop && terminalLoopIds.has(parentLoop) && status !== "error") {
        status = "complete";
      }

      return {
        id: key,
        stepType: s.step_type ?? "agent",
        label: (s.step_type === "agent" ? (s.agent_display_name ?? s.step_name) : s.step_name) ?? key,
        stepName: s.step_name ?? key,
        agentUuid: s.agent_id ?? undefined,
        status,
        startedAt: s.started_at,
        completedAt: s.completed_at ?? null,
        outputPreview: s.output_preview,
        promptPreview: s.prompt_preview,
        errorMessage: s.error ?? null,
        agentVersion: s.agent_version,
        agentRunNumber: s.agent_run_number,
        approvalId: s.approval_id ?? null,
        parentLoop,
      };
    });

  // ── Inject missing body steps for active loop steps ──────────────────────
  // The backend only commits body-step logs at checkpoints/completion, so
  // during execution the stepsMap may only contain the loop step itself.
  // For any loop step that is running/paused, pre-inject its body steps
  // (from dagDefinition) as "queued" so they appear in the correct position
  // before the loop step, even before the backend has written their logs.
  const startedIds = new Set(startedSteps.map((s) => s.id));

  if (dagDefinition?.steps) {
    const dagStepById = new Map<string, any>();
    dagDefinition.steps.forEach((s: any) => { if (s?.id) dagStepById.set(s.id, s); });

    startedSteps.forEach((loopStep) => {
      if (loopStep.stepType !== "loop") return;
      // Only inject for non-terminal loop steps
      if (loopStep.status === "complete" || loopStep.status === "error") return;

      const dagLoopStep = dagStepById.get(loopStep.id);
      if (!dagLoopStep) return;

      const bodyIds: string[] = dagLoopStep.loop_body_steps ?? [];
      // Sort body IDs by dagOrder so we mark the right one as "running"
      const sortedBodyIds = [...bodyIds].sort(
        (a, b) => (dagOrder.get(a) ?? 999) - (dagOrder.get(b) ?? 999)
      );
      let firstUnstarted = true;
      sortedBodyIds.forEach((bid: string) => {
        if (startedIds.has(bid)) return; // already in stepsMap with started_at

        const dagBodyStep = dagStepById.get(bid);
        // First not-yet-started body step → running (it's what the loop is executing)
        // Remaining body steps → queued
        const injectedStatus: DerivedStep["status"] = firstUnstarted ? "running" : "queued";
        firstUnstarted = false;

        startedSteps.push({
          id: bid,
          stepType: dagBodyStep?.type ?? "agent",
          label: dagBodyStep?.name ?? dagBodyStep?.description ?? bid,
          status: injectedStatus,
          startedAt: null,
          completedAt: null,
          outputPreview: undefined,
          promptPreview: undefined,
          errorMessage: null,
          agentVersion: dagBodyStep?.agent_version,
          agentRunNumber: undefined,
          parentLoop: loopStep.id,
        });
        startedIds.add(bid);
      });
    });
  }

  // ── Sort by stable DAG display order ────────────────────────────────────
  // dagOrder is purely structure-based — never changes between re-renders.
  // Steps not in dagOrder (shouldn't happen) fall to the end.
  startedSteps.sort((a, b) => {
    const ai = dagOrder.get(a.id) ?? 999;
    const bi = dagOrder.get(b.id) ?? 999;
    return ai - bi;
  });

  // ── Queued step (next not-yet-started top-level step) ───────────────────
  const isTerminalRun =
    runDetails.status === "completed" || runDetails.status === "failed" ||
    runDetails.status === "cancelled" || runDetails.status === "error";

  if (!isTerminalRun) {
    // startedIds already includes injected body steps — only pick top-level
    // steps that haven't started and aren't body steps of an active loop
    const notStartedKeys = allKeys.filter((k) => !startedIds.has(k) && !bodyStepParent.has(k));

    // Pick the next one by DAG display order
    const nextKey = notStartedKeys.sort((a, b) =>
      (dagOrder.get(a) ?? 999) - (dagOrder.get(b) ?? 999)
    )[0];

    if (nextKey) {
      const s = stepsMap[nextKey];
      startedSteps.push({
        id: nextKey,
        stepType: s.step_type ?? "agent",
        label: s.step_name ?? nextKey,
        stepName: s.step_name ?? nextKey,
        status: "queued",
        startedAt: null,
        completedAt: null,
        outputPreview: undefined,
        promptPreview: s.prompt_preview,
        errorMessage: null,
        agentVersion: s.agent_version,
        agentRunNumber: undefined,
      });
    }
  }

  return startedSteps;
}

// ─── Component ────────────────────────────────────────────────────────────────

const WorkflowRunTimeline: React.FC<WorkflowRunTimelineProps> = ({
  runDetails,
  workflowName,
  dagDefinition,
}) => {
  const errorRed  = "#d32f2f";
  const queuedGray = "#94a3b8";

  const steps = useMemo(() => deriveSteps(runDetails, dagDefinition), [runDetails, dagDefinition]);

  // ── Agent display name lookup ──────────────────────────────────────────────
  // Collect unique agent_ids from BOTH the DAG definition and the execution log,
  // fetch their display_name once, and overlay onto step labels.
  // Using dagDefinition means names are resolved before any step even starts.
  const [agentNames, setAgentNames] = useState<Record<string, string>>({});
  useEffect(() => {
    // IDs from execution log (for completed/in-progress runs)
    const stepsMap: Record<string, any> = (runDetails?.execution_logs as any)?.steps ?? {};
    const fromLog = Object.values(stepsMap)
      .filter((s: any) => s.step_type === "agent" && s.agent_id)
      .map((s: any) => s.agent_id as string);

    // IDs from DAG definition (available immediately, before any step runs)
    const fromDag = (dagDefinition?.steps ?? [])
      .filter((s: any) => s?.type === "agent" && s?.agent_id && String(s.agent_id).toUpperCase() !== "PLACEHOLDER")
      .map((s: any) => s.agent_id as string);

    const agentIds = Array.from(new Set([...fromDag, ...fromLog]));
    if (agentIds.length === 0) return;
    let cancelled = false;
    Promise.all(
      agentIds.map((id) =>
        getAgent(id)
          .then((res: any) => {
            const rec = Array.isArray(res?.agents) ? res.agents[0] : res?.agent ?? res;
            return [id, rec?.display_name || rec?.name] as [string, string];
          })
          .catch(() => [id, null] as [string, null])
      )
    ).then((pairs) => {
      if (cancelled) return;
      const map: Record<string, string> = {};
      pairs.forEach(([id, name]) => { if (id && name) map[id] = name; });
      setAgentNames(map);
    });
    return () => { cancelled = true; };
  }, [runDetails, dagDefinition]);

  // Overlay agent display names onto steps, and use DAG step.name as the short stepName
  const enrichedSteps = useMemo(() => {
    const dagStepById = new Map<string, any>();
    (dagDefinition?.steps ?? []).forEach((s: any) => { if (s?.id) dagStepById.set(s.id, s); });

    return steps.map((step) => {
      const dagStep = dagStepById.get(step.id);
      // Short name from DAG (same field the visualization uses), falling back to step ID prettified
      const dagName: string | undefined = dagStep?.name
        ?? step.id.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());

      if (step.stepType !== "agent") {
        // For non-agent steps, stepName = DAG short name (already is the label, skip redundancy)
        return { ...step, stepName: dagName };
      }

      const stepsMap: Record<string, any> = (runDetails?.execution_logs as any)?.steps ?? {};
      // Prefer agentUuid already on the step (set from execution_log.agent_id),
      // fall back to the DAG step's agent_id so names resolve before the step starts.
      const dagStep2 = dagStepById.get(step.id);
      const agentId: string | undefined =
        step.agentUuid ?? stepsMap[step.id]?.agent_id ?? dagStep2?.agent_id;
      const displayName = (agentId && agentNames[agentId]) || undefined;
      // label = agent display_name; stepName = DAG short name shown as sub-line
      return { ...step, label: displayName ?? step.label, stepName: dagName };
    });
  }, [steps, agentNames, runDetails, dagDefinition]);
  const [agentPanel, setAgentPanel] = useState<{
    open: boolean; agentId: string; agentVersion?: number; agentRunNumber?: number; label: string;
    workflowId?: string; workflowVersion?: number;
  }>({ open: false, agentId: "", label: "" });

  const [approvalPanel, setApprovalPanel] = useState<{
    open: boolean; approvalId: string; stepId: string; label: string;
    workflowRunId?: string; workflowVersion?: number; workflowRunNumber?: number;
  }>({ open: false, approvalId: "", stepId: "", label: "" });

  // ── Progress: use TOTAL known steps as denominator, not just started ones ──
  const totalStepsInRun: number = Object.keys(
    (runDetails?.execution_logs as any)?.steps ?? {}
  ).length;
  const completedCount = steps.filter((s) => s.status === "complete").length;
  const overallStatus  = runDetails?.status ?? null;
  const isTerminal     =
    overallStatus === "completed" || overallStatus === "failed" ||
    overallStatus === "cancelled" || overallStatus === "error";

  // Any terminal state → 100% fill. The bar color already distinguishes
  // success (green) from failure (red). While running → indeterminate animation.
  const denom   = totalStepsInRun > 0 ? totalStepsInRun : steps.length;
  const progress = isTerminal
    ? 100
    : denom === 0 ? 0 : Math.round((completedCount / denom) * 100);

  // ── Empty / no-run state ───────────────────────────────────────────────────
  if (!runDetails) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 2, color: "text.secondary" }}>
        <PsychologyIcon sx={{ fontSize: 48, opacity: 0.25 }} />
        <Typography variant="body2">Select a run to view its execution timeline.</Typography>
      </Box>
    );
  }

  if (steps.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 2, color: "text.secondary" }}>
        <CircularProgress size={28} thickness={4} sx={{ color: "#4F6AF0" }} />
        <Typography variant="body2">Waiting for steps to start…</Typography>
      </Box>
    );
  }

  return (
    <>
    <Box sx={{
      mx: 2, mt: 2, px: 2, pt: 1.5, pb: 2,
      overflowY: "auto",
      overflowX: "hidden",
      position: "absolute",
      inset: 0,
      scrollbarGutter: "stable",
      "&::-webkit-scrollbar": { width: "6px" },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(0,0,0,0.1)",
        borderRadius: "10px",
        "&:hover": { backgroundColor: "rgba(0,0,0,0.25)" },
      },
    }}>

      {/* ── Full-width header with text label + progress bar ──────────────── */}
      <Box
        sx={{
          mb: 2,
          // px: 2,
          py: 1,
          background: "rgba(255,255,255,0.6)",
          backdropFilter: "blur(8px)",
        }}
      >
        {/* Top row: title + step count */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: "#B87333", display: "flex", alignItems: "center", gap: 0.75 }}
          >
            <PsychologyIcon sx={{ fontSize: 16 }} />
            {workflowName ? `${workflowName} — Execution` : "Execution Timeline"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#4F6AF0", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {overallStatus && (
              <span style={{ marginLeft: 8, textTransform: "capitalize", opacity: 0.65, fontWeight: 500 }}>
                {overallStatus}
              </span>
            )}
          </Typography>
        </Box>
        {/* Progress bar */}
        <LinearProgress
          variant={isTerminal ? "determinate" : "indeterminate"}
          value={isTerminal ? progress : undefined}
          sx={{
            height: 7,
            borderRadius: 4,
            bgcolor: "rgba(79,106,240,0.1)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              background:
                overallStatus === "failed" || overallStatus === "error"
                  ? `linear-gradient(90deg, ${errorRed} 0%, #ff6b6b 100%)`
                  : overallStatus === "completed"
                  ? "linear-gradient(90deg, #2E8B57 0%, #52c282 100%)"
                  : "linear-gradient(90deg, #4F6AF0 0%, #818CF8 100%)",
            },
            "& .MuiLinearProgress-bar1Indeterminate": {
              background: "linear-gradient(90deg, #4F6AF0 0%, #818CF8 100%)",
            },
            "& .MuiLinearProgress-bar2Indeterminate": {
              background: "linear-gradient(90deg, #818CF8 0%, #4F6AF0 100%)",
            },
          }}
        />
      </Box>

      {/* ── Timeline — no opposite content; timestamp lives inside card ──── */}
      <Timeline position="right" sx={{ p: 0, m: 0,  }}>
        {enrichedSteps.map((step, index) => {
          const isRunning = step.status === "running";
          const isError   = step.status === "error";
          const isQueued  = step.status === "queued";
          const isDone    = step.status === "complete";
          const isPaused  = step.status === "paused";
          const isWaiting = step.status === "waiting";
          const isPausedOrWaiting = isPaused || isWaiting;

          const colors = stepColor(step.stepType);
          const pausedAmber = "#E67E22";

          const activeDotBg     = isError ? errorRed : isRunning ? "white" : isPausedOrWaiting ? "white" : isQueued ? "white" : colors.dot;
          const activeDotBorder = isError ? errorRed : isRunning ? colors.dot : isPausedOrWaiting ? pausedAmber : isQueued ? queuedGray : colors.dot;
          const activeCardBg    = isError ? "rgba(211,47,47,0.05)" : isPausedOrWaiting ? "rgba(230,126,34,0.06)" : isQueued ? "rgba(148,163,184,0.05)" : colors.bg;
          const activeCardBorder= isError ? "rgba(211,47,47,0.25)" : isPausedOrWaiting ? "rgba(230,126,34,0.3)" : isQueued ? "rgba(148,163,184,0.3)" : colors.border;
          const connectorColor  = isQueued ? "transparent" : `${colors.dot}33`;

          const hasDetails = !isQueued && (step.promptPreview || step.outputPreview || isError
            || (step.stepType === "agent" && step.agentRunNumber != null)
            || (step.stepType === "human_input" && step.approvalId != null));

          return (
            <TimelineItem key={step.id} sx={{ minHeight: 0, "&:before": { display: "none" } }}>

              <TimelineSeparator>
                {/* Top connector — always flex:1; invisible on first item so the line doesn't start above it */}
                <TimelineConnector
                  sx={{
                    width: "2px",
                    ...(index === 0
                      ? { bgcolor: "transparent" }
                      : isQueued
                      ? { bgcolor: "transparent", borderLeft: `2px dashed ${queuedGray}` }
                      : { bgcolor: connectorColor }),
                  }}
                />
                <TimelineDot
                  sx={{
                    p: "5px",
                    background: activeDotBg,
                    border: `2px ${isQueued ? "dashed" : "solid"} ${activeDotBorder}`,
                    boxShadow: isRunning ? `0 0 8px ${colors.dot}88` : "none",
                    opacity: isQueued ? 0.6 : 1,
                    transition: "all 0.25s ease",
                    my: "6px",
                    flexShrink: 0,
                  }}
                >
                  {isRunning ? (
                    <CircularProgress size={13} thickness={5} sx={{ color: colors.dot }} />
                  ) : isError ? (
                    <ErrorOutlineIcon sx={{ fontSize: 14, color: "white" }} />
                  ) : isPausedOrWaiting ? (
                    <PauseCircleOutlineIcon sx={{ fontSize: 14, color: pausedAmber }} />
                  ) : isQueued ? (
                    <HourglassEmptyIcon sx={{ fontSize: 14, color: queuedGray }} />
                  ) : (
                    <Box sx={{ fontSize: 14, color: isDone ? "white" : colors.dot, display: "flex" }}>
                      {stepIcon(step.stepType, 14)}
                    </Box>
                  )}
                </TimelineDot>
                {/* Bottom connector — always flex:1; invisible on last item so line doesn't trail off */}
                <TimelineConnector
                  sx={{
                    width: "2px",
                    ...(index === enrichedSteps.length - 1
                      ? { bgcolor: "transparent" }
                      : enrichedSteps[index + 1]?.status === "queued"
                      ? { bgcolor: "transparent", borderLeft: `2px dashed ${queuedGray}` }
                      : { bgcolor: connectorColor }),
                  }}
                />
              </TimelineSeparator>

              {/* Step card */}
              <TimelineContent sx={{ py: "8px", pl: 1.5, pr: 0 }}>
                <Accordion
                  elevation={0}
                  defaultExpanded={false}
                  disabled={!isRunning && (isQueued || !hasDetails)}
                  disableGutters
                  sx={{
                    background: activeCardBg,
                    border: "1px solid",
                    borderStyle: isQueued ? "dashed" : "solid",
                    borderColor: activeCardBorder,
                    borderRadius: "10px !important",
                    opacity: isQueued ? 0.65 : 1,
                    transition: "all 0.15s ease",
                    "&:hover": isQueued ? {} : { transform: "translateX(2px)" },
                    "&:before": { display: "none" },
                    "&.Mui-disabled": { opacity: isQueued ? 0.65 : 1, bgcolor: "transparent" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={hasDetails ? <ExpandMoreIcon sx={{ fontSize: 16, color: colors.dot }} /> : null}
                    sx={{
                      minHeight: "44px",
                      py: 0,
                      px: 1.5,
                      cursor: hasDetails ? "pointer" : "default !important",
                      "& .MuiAccordionSummary-content": { my: "10px", flexDirection: "column", gap: 0.75 },
                    }}
                  >
                    {/* Row 1: icon + label + timestamp */}
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
                    {/* Step type icon */}
                    <Box sx={{ fontSize: 15, color: isQueued ? queuedGray : isError ? errorRed : colors.dot, flexShrink: 0, display: "flex", mt: "2px" }}>
                      {stepIcon(step.stepType, 15)}
                    </Box>

                    {/* Label + step type sub-line */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.82rem",
                          color: isError ? errorRed : isQueued ? "#64748b" : colors.text,
                          lineHeight: 1.3,
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {step.label}
                        {step.agentVersion != null && (
                          <span style={{ fontWeight: 400, opacity: 0.5, marginLeft: 5, fontSize: "0.72rem" }}>
                            v{step.agentVersion}
                          </span>
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: "0.68rem", color: "text.disabled", lineHeight: 1, textTransform: "capitalize" }}>
                        {step.stepType.replace("_", " ")}
                        {step.stepName && step.stepName !== step.label && (
                          <span style={{ textTransform: "none", marginLeft: 4, opacity: 0.75 }}>
                            · {step.stepName}
                          </span>
                        )}
                      </Typography>
                    </Box>

                    {/* Right: timestamp + status badge */}
                    <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25, pr: 1 }}>
                      {/* Timestamp + duration on the same line */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: "0.67rem", color: "text.disabled", fontVariantNumeric: "tabular-nums" }}>
                          {isQueued ? <em style={{ color: queuedGray }}>next</em> : formatTime(step.startedAt)}
                        </Typography>
                        {isDone && (
                          <Typography variant="caption" sx={{ fontSize: "0.67rem", color: colors.dot, opacity: 0.8, fontVariantNumeric: "tabular-nums" }}>
                            · {durationLabel(step.startedAt, step.completedAt)}
                          </Typography>
                        )}
                        {isError && step.startedAt && (
                          <Typography variant="caption" sx={{ fontSize: "0.67rem", color: errorRed, opacity: 0.7, fontVariantNumeric: "tabular-nums" }}>
                            · {durationLabel(step.startedAt, step.completedAt)}
                          </Typography>
                        )}
                      </Box>
                      {/* Status */}
                      {isRunning && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                          <CircularProgress size={9} thickness={5} sx={{ color: colors.dot }} />
                          <Typography variant="caption" sx={{ fontSize: "0.67rem", color: colors.dot, fontWeight: 600 }}>running</Typography>
                        </Box>
                      )}
                      {isWaiting && (
                        <Typography variant="caption" sx={{ fontSize: "0.67rem", color: pausedAmber, fontWeight: 600 }}>
                          waiting for review
                        </Typography>
                      )}
                      {isPaused && (
                        <Typography variant="caption" sx={{ fontSize: "0.67rem", color: pausedAmber, fontWeight: 600 }}>
                          paused
                        </Typography>
                      )}
                      {isError && (
                        <Typography variant="caption" sx={{ fontSize: "0.67rem", color: errorRed, fontWeight: 600 }}>failed</Typography>
                      )}
                    </Box>
                    </Box>

                    {/* Row 2: action buttons — always visible inside the summary */}
                    {((step.stepType === "agent" && step.agentRunNumber != null) || (step.stepType === "human_input" && step.approvalId)) && (
                      <Box
                        sx={{ display: "flex", justifyContent: "flex-end", width: "100%", pb: 0.25 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {step.stepType === "agent" && step.agentRunNumber != null && (
                          <Box
                            component="button"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setAgentPanel({ open: true, agentId: step.agentUuid ?? step.id, agentVersion: step.agentVersion, agentRunNumber: step.agentRunNumber, label: step.label, workflowId: runDetails?.workflow_id ? String(runDetails.workflow_id) : undefined, workflowVersion: runDetails?.workflow_version ?? undefined });
                            }}
                            sx={{
                              border: `1px solid ${colors.border}`,
                              bgcolor: colors.bg,
                              color: colors.text,
                              borderRadius: "7px",
                              px: 1.25, py: 0.3,
                              mr: 1,
                              fontSize: "0.68rem", fontWeight: 600,
                              cursor: "pointer",
                              "&:hover": { filter: "brightness(0.92)" },
                              display: "flex", alignItems: "center", gap: 0.4,
                            }}
                          >
                            <SmartToyIcon sx={{ fontSize: 11 }} />
                            Details
                          </Box>
                        )}
                        {step.stepType === "human_input" && step.approvalId && (
                          <Box
                            component="button"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setApprovalPanel({ open: true, approvalId: step.approvalId!, stepId: step.id, label: step.label, workflowRunId: runDetails?.id ? String(runDetails.id) : undefined, workflowVersion: runDetails?.workflow_version ?? undefined, workflowRunNumber: runDetails?.run_number ?? undefined });
                            }}
                            sx={{
                              border: "1px solid rgba(142,88,142,0.3)",
                              bgcolor: "rgba(142,88,142,0.07)",
                              color: "#5a3060",
                              borderRadius: "7px",
                              px: 1.25, py: 0.3,
                              mr: 1,
                              fontSize: "0.68rem", fontWeight: 600,
                              cursor: "pointer",
                              "&:hover": { filter: "brightness(0.92)" },
                              display: "flex", alignItems: "center", gap: 0.4,
                            }}
                          >
                            <PersonOutlineIcon sx={{ fontSize: 11 }} />
                            Approvals
                          </Box>
                        )}
                      </Box>
                    )}
                  </AccordionSummary>

                  {/* Expandable details */}
                  {hasDetails && (
                    <AccordionDetails
                      sx={{
                        bgcolor: "rgba(0,0,0,0.025)",
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                        borderBottomLeftRadius: "10px",
                        borderBottomRightRadius: "10px",
                        p: 1.25,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {step.promptPreview && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#888", display: "block", mb: 0.25 }}>Prompt</Typography>
                          <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "0.70rem", color: "#555", bgcolor: "rgba(0,0,0,0.03)", p: 0.75, borderRadius: "5px", maxHeight: 100, overflowY: "auto", m: 0, "&::-webkit-scrollbar": { width: "4px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.15)", borderRadius: "10px" } }}>
                            {step.promptPreview}
                          </Typography>
                        </Box>
                      )}
                      {step.outputPreview && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#888", display: "block", mb: 0.25 }}>Output</Typography>
                          <Box
                            sx={{
                              bgcolor: `${colors.dot}0d`,
                              p: 0.75,
                              borderRadius: "5px",
                              maxHeight: 240,
                              overflowY: "auto",
                              fontSize: "0.72rem",
                              color: "#334155",
                              lineHeight: 1.6,
                              "&::-webkit-scrollbar": { width: "4px" },
                              "&::-webkit-scrollbar-track": { background: "transparent" },
                              "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.15)", borderRadius: "10px" },
                              "& p": { m: 0, mb: 0.5 },
                              "& p:last-child": { mb: 0 },
                              "& ul, & ol": { pl: 2, m: 0, mb: 0.5 },
                              "& li": { mb: 0.25 },
                              "& code": { bgcolor: "rgba(0,0,0,0.06)", px: 0.4, py: 0.1, borderRadius: "3px", fontFamily: "monospace", fontSize: "0.68rem" },
                              "& pre": { bgcolor: "rgba(0,0,0,0.05)", p: 0.75, borderRadius: "4px", overflowX: "auto", m: 0, mb: 0.5 },
                              "& pre code": { bgcolor: "transparent", p: 0 },
                              "& h1, & h2, & h3, & h4": { m: 0, mb: 0.5, fontWeight: 700, fontSize: "0.78rem" },
                              "& strong": { fontWeight: 700 },
                              "& blockquote": { borderLeft: "3px solid rgba(0,0,0,0.15)", pl: 1, ml: 0, color: "#64748b" },
                              "& a": { color: colors.dot },
                            }}
                          >
                            <ReactMarkdown>{step.outputPreview}</ReactMarkdown>
                          </Box>
                        </Box>
                      )}
                      {isError && (
                        <Typography variant="caption" sx={{ color: errorRed, display: "flex", alignItems: "center", gap: 0.5 }}>
                          <ErrorOutlineIcon sx={{ fontSize: 13 }} />
                          {step.errorMessage ?? "This step failed."}
                        </Typography>
                      )}
                    </AccordionDetails>
                  )}
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Box>

    {/* ── Agent detail panel ──────────────────────────────────────────── */}
    {agentPanel.open && (
      <StepAgentDetailPanel
        open={agentPanel.open}
        onClose={() => setAgentPanel((p) => ({ ...p, open: false }))}
        agentId={agentPanel.agentId}
        agentVersion={agentPanel.agentVersion}
        initialRunNumber={agentPanel.agentRunNumber}
        stepLabel={agentPanel.label}
        workflowId={agentPanel.workflowId}
        workflowVersion={agentPanel.workflowVersion}
      />
    )}

    {/* ── Approval detail panel ──────────────────────────────────────── */}
    {approvalPanel.open && (
      <StepApprovalDetailPanel
        open={approvalPanel.open}
        onClose={() => setApprovalPanel((p) => ({ ...p, open: false }))}
        approvalId={approvalPanel.approvalId}
        stepId={approvalPanel.stepId}
        stepLabel={approvalPanel.label}
        workflowRunId={approvalPanel.workflowRunId}
        workflowVersion={approvalPanel.workflowVersion}
        workflowRunNumber={approvalPanel.workflowRunNumber}
      />
    )}
  </>
  );
};

export default WorkflowRunTimeline;
